(ns ycweb.components.forms
  (:require
   [ajax.core :as ajax]
   [clojure.edn :as edn]
   [inflections.core :as in]
   #?@(:cljs
       [[cljs-time.format :as dtfmt]
        [cljs-time.coerce :as dtcoerce]]
       :clj
       [[clj-time.format :as dtfmt]
        [clj-time.coerce :as dtcoerce]])
   [rum.core :as rum]
   [util.string :as str]
   [util.transform :as trf]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.util.core :as u]
   [ycweb.util.date :as ud]))

;; From input field to value
(defn coerce-input [tp v]
  (case tp
    :currency (u/parseFloat v)
    :tocurrency (trf/tocurrency v)
    :fromcurrency (trf/fromcurrency v)
    :number (when (u/presence v) (u/parseFloat v))
    :date (when v (ud/from-default-time-zone (dtfmt/parse (dtfmt/formatter "dd-MM-YYYY HH:mm") v)))
    :datedmy (when v (ud/from-default-time-zone (dtfmt/parse (dtfmt/formatter "dd-MM-YYYY") v)))
    :dateword (when v (ud/from-default-time-zone (dtfmt/parse (dtfmt/formatter "dd-MMM-YYYY") v)))
    :date/dmy (when v (ud/from-default-time-zone (dtfmt/parse (dtfmt/formatter "dd/MMM/YYYY") v)))
    :keyword (when v (edn/read-string v))
    :uuid (u/uuid v)
    v))

;; Value to input field
(defn coerce-output [tp v]
  (case tp
    :tocurrencyformat (u/number-format (trf/fromcurrency v) 2)
    :tocurrency (trf/fromcurrency v)
    :fromcurrency (trf/tocurrency v)
    :datedmy
    (if v
      (dtfmt/unparse
        (dtfmt/formatter "dd-MM-YYYY")
        (ud/to-default-time-zone
          (dtcoerce/to-date-time v)))
      "")

    :date
    (if v
      (dtfmt/unparse
        (dtfmt/formatter "dd-MM-YYYY HH:mm")
        (ud/to-default-time-zone
          (dtcoerce/to-date-time v)))
      "")
    :dateword
    (if v
      (dtfmt/unparse
        (dtfmt/formatter "dd MMM YYYY")
        (ud/to-default-time-zone
          (dtcoerce/to-date-time v)))
      "")
    :date/dmy
    (if v
      (dtfmt/unparse
        (dtfmt/formatter "dd/MM/YYYY")
        (ud/to-default-time-zone
          (dtcoerce/to-date-time v)))
      "")
    :keyword (str v)
    :uuid (str v)
    v))

(defn input-props
  [atm {:keys [change-fn default submit-fn onblur-fn on-key-down yc-input? error-atom] :as options}]
  (u/maybe
    assoc
    {:value
     (let [value (when atm (rum/react atm))]
       (if value
         (coerce-output (or (:type options) :text) value)
         (if default
           (do
             (reset! atm default)
             (when change-fn
               (change-fn nil default))
             (or default ""))
           "")))

     :on-change
     (fn [e]
       (let [v (coerce-input (:type options) (.. e -target -value))]
         (reset! atm v)
         (when (and yc-input? error-atom)
           (reset! error-atom nil))
         (when change-fn
           (change-fn e v))))}
    :on-blur
    (fn [e]
      (when onblur-fn
        (let [v (coerce-input (:type options) (.. e -target -value))]
          (onblur-fn e (u/presence v)))))
    :tab-index 0
    :on-key-down
    (fn [e]
      (let [v (coerce-input (:type options) (.. e -target -value))]
        (cond
          on-key-down
          (on-key-down e v)

          :else
          (when submit-fn
            (fn [e]
              #?(:cljs
                 (when (== (.-keyCode e) 13)
                   (js/setTimeout
                     #(submit-fn v)
                     200)

                   (.preventDefault e)))
              false)))))))

(defn checkbox-props
  ([atm] (checkbox-props atm nil nil nil nil))
  ([atm change-fn pre-change-fn disabled required?]
   {:checked (boolean (rum/react atm))
    :type "checkbox"
    :disabled (when disabled true)
    :required required?
    :on-click #(when pre-change-fn (pre-change-fn))
    :on-change
    (fn [e]
      (swap! atm not)
      (when change-fn
        (change-fn e)))}))

(defn toggle-props
  ([atm] (toggle-props atm nil nil nil nil nil))
  ([atm default-checked change-fn disabled required? on-change]
   (let [prop-value (rum/react atm)]
     {:checked
      (boolean
        (cond
          (and
            (nil? prop-value)
            default-checked)
          default-checked

          :else
          prop-value))
      :type "checkbox"
      :disabled (when disabled true)
      :required required?
      :on-change
      (or
        on-change
        (fn [e]
          (swap! atm not)
          (when change-fn
            (change-fn e))))})))

;; No.
(defn colonify
  "Ensures that the input ends with a colon.
  If the trimmed input already ends with a colon, it will remain unchanged."
  [input]
  (when-let [present-string (u/presence input)]
    (if (string? present-string)
      (str present-string (if (re-matches #".*\:$" present-string) "" ":"))
      (str present-string))))

(rum/defcs input-raw
  < rum/reactive
  (rum/local false ::visibility)
  {:will-mount
   (fn [state]
     (let [[_ val-atom {:keys [change-fn]}] (:rum/args state)
           local-atom (atom @val-atom)
           component (:rum/react-component state)]
         ;; Setup two-way atom binding
       (add-watch val-atom ::input
         (fn [k r o n]
           (when (and (not= o n) (not= @local-atom n))
             (reset! local-atom n)
             (rum/request-render component))))

       (cmn/debounce-atom local-atom 150
         (fn [n]
           (when (not= @val-atom n)
             (reset! val-atom n)
             (when change-fn
               (change-fn n)))))

       (assoc state ::value local-atom)))

   :after-render
   (fn [state]
     #?(:cljs
        (let [[_ _ options] (:rum/args state)]
          (when (:auto-focus options)
            (let [node (js/ReactDOM.findDOMNode (:rum/react-component state))]
              (when
                (and

                  node
                  (::visibility state)
                  (not @(::visibility state))
                  (not= (.-display (.-style node)) "none"))
                (reset! (::visibility state) true)
                (js/setTimeout #(.focus node) 100))))))
     state)}

  [s placeholder value-atom
   ;; type, name and class are also options...
   {:keys
    [id datatype hint on-dark required? yc-input? change-fn autocomplete
     auto-focus default disabled on-focus max-length read-only style
     submit-fn size no-border onblur-fn no-icon on-key-down error-atom] :as options
    :or {autocomplete "off"}}]
  (let [val-atom (::value s)
        icon? (and (:type options) (not= (:type options) :text))]
    [:input
     (merge
       (input-props val-atom
         {:type (or datatype (:type options))
          :default default
          :change-fn change-fn
          :yc-input? yc-input?
          :error-atom error-atom
          :submit-fn submit-fn
          :onblur-fn onblur-fn
          :on-key-down on-key-down})
       {:type
        (case (:type options)
          :tocurrency "number"
          :fromcurrency "number"
          :currency "number"
          :text-number "text"
          :date "text"
          :phone "text"
          :password "password"
          :hidden "hidden"
          :twittername "text"
          :facebook "text"
          :url "url"
          :search "search"
          (u/safe-name (or (:type options) :text)))
        :id id
        :name (:name options)
        :style style
        :class
        (u/bem
          (when-not
            no-border
            :text-input)
          [(when (and icon? (not no-icon)) :has-icon)
           (when on-dark :on-dark)
           (case (:size options)
             :small :small
             nil)
           (case (:type options)
             :date :calendar
             :user :user
             :barcode :barcode-scan
             (:tocurrencyformat :tocurrency :currency) :zar
             :location :location
             :phone :tel
             :password :password
             :twittername :twitter
             :facebookname :facebook
             :url :website
             nil)]
          (:class options))
        :pattern (case (:type options) :user ".*\\S.*" :text-number "[0-9]*" (:tel :phone) "^(?!\\s)[+]{0,1}[0-9\\s]{9,}" nil)
        :inputMode (case (:type options) :text-number "numeric" nil)
        :title
        (cond
          (#{:tel :phone} (:type options))
          "Phone number can not contain alphabetical or special characters."

          (= (:type options) :user)
          "Name can not be blank.")
        :max-length max-length
        :placeholder placeholder
        :size size
        :required required?
        :disabled disabled
        :read-only read-only
        :on-focus on-focus
        :auto-focus auto-focus
        :autoComplete autocomplete
        :spellCheck "false"
        :autoCorrect "off"
        :autoCapitalize "off"})]))

(rum/defc field-label < rum/reactive [label {:keys [on-dark label-hidden?] :as options}]
  (let [for-name (when (u/presence (:name options)) (name (:name options)))]
    [:label.form-label
     {:for for-name
      :class
      (u/bem
        (when on-dark :u-text-white) []
        (when label-hidden? :u-hidden-from-view))}
     (cmn/curval label)]))

(rum/defc form-field < rum/reactive [label {:keys [field-classes error error-atom flush?] :as options} & body]
  (let [error-msg (when error-atom (rum/react error-atom))]
    (into
      [:div
       {:class (u/bem :form-field [(when (or error error-msg) :error) (when flush? :flush) (when field-classes field-classes)])}
       (field-label label options)]
      body)))

(rum/defc error-block < rum/reactive [error-atom]
  (let [error (when error-atom (rum/react error-atom))]
    (when error
      [:div.form-hint-wrapper
       [:span.form-hint error]])))

(rum/defcs input
  < rum/reactive (rum/local false ::email-expanded)
  [s label placeholder field-atom
   & [{:keys
       [hint show-email-helpers required?
        label-hidden? change-fn on-dark small-width error on-focus no-icon flush? yc-input?] :as options}]]
  (let [val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        error-atom (when yc-input? (rum/cursor field-atom :error-msg))
        tp (:type options)
        icon? (and tp (not= tp :text))
        email (fn [suffix] #(swap! val-atom (fn [s] (str (or (second (re-find #"([^@]*)[@].*" (str s))) s) suffix))))
        options-with-error (u/maybe assoc options :error-atom error-atom)
        expanded-atom (::email-expanded s)
        expanded? (rum/react expanded-atom)
        email-btn
        (fn [[t e]]
          [:a
           {:class (u/bem :button [:bordered :secondary :tiny :flat])
            :on-click (email e)}
           [:span.button__text t]])]
    (form-field label
      options-with-error
      [:div.text-input-wrapper
       {:class (when small-width "u-max-width-128")}
       (input-raw placeholder val-atom options-with-error)

       (when (and icon? (not no-icon))
         [:i.text-input-icon])]

      (when (and (= tp :email) show-email-helpers)
        [:div
         (into
           [:div.form-helper-buttons]
           (conj
             (mapv email-btn
               [["@gmail" "@gmail.com"] ["@yahoo" "@yahoo.com"] ["@hotmail" "@hotmail.com"] ["@icloud" "@icloud.com"]])
             [:a
              {:class (u/bem :button [:bordered :secondary :tiny :flat])
               :on-click #(swap! expanded-atom not)}
              [:span.button__text (if expanded? "Less ▲" "More ▼")]]))
         (when expanded?
           [:div
            (into
              [:div.form-helper-buttons]
              (mapv email-btn
                [["@mweb" "@mweb.co.za"] ["@telkomsa" "@telkomsa.net"] ["@outlook" "@outlook.com"]]))])])

      (when hint
        [:div.form-hint-wrapper
         [:span.form-hint hint]])
      (when error
        [:div.form-hint-wrapper
         [:span.form-hint error]])

      (error-block error-atom))))

(rum/defcs password-input
  "options
   :autocomplete - defaults to 'off', pass in 'new-password' to prevent browser pre-filling"
  < rum/reactive (rum/local {} ::local)
  [s label placeholder field-atom & [{:keys [hint autocomplete yc-input?] :as options}]]
  (let [local-atom (::local s)
        local (rum/react local-atom)
        password-visible? (:password-visible? local)
        val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        error-atom (when yc-input? (rum/cursor field-atom :error-msg))]

    (form-field label
      (u/maybe assoc options :error-atom error-atom)
      [:div.text-input-wrapper
       (input-raw placeholder val-atom
         (assoc options
           :type (if password-visible? :text :password)
           :yc-input? yc-input?
           :error-atom error-atom))

       [:button.password-toggle-button
        {:type "button"
         :on-click #(swap! local-atom update :password-visible? not)}
        [:span.u-hidden-from-view
         (if password-visible? "Hide password" "Show password")]
        [:i.password-toggle-button__icon
         {:class (u/bem :icon [(if password-visible? :eye-off :eye)])}]]]

      (when hint
        [:div.form-hint-wrapper
         [:span.form-hint hint]])
      (error-block error-atom))))

(rum/defc split-field < rum/reactive
  [[llabel left-component latom loptions :as left]
   [rlabel right-component ratom roptions :as right]]
  (let [l-error-atom (when (:yc-input? loptions) (rum/cursor latom :error-msg))
        r-error-atom (when (:yc-input? roptions) (rum/cursor ratom :error-msg))
        l-error-msg (when l-error-atom (rum/react l-error-atom))
        r-error-msg (when r-error-atom (rum/react r-error-atom))]
    [:div
     {:class
      (u/bem :form-field
        [(when (or (:flush? loptions) (:flush? roptions)) :flush)])}
     [:div.form-label-group
      [:div.form-label-group__column
       (field-label llabel loptions)]
      [:div.form-label-group__column
       (field-label rlabel roptions)]]

     [:div.text-input-group
      [:div.text-input-group__column
       {:class
        (u/bem :input-group [(when l-error-msg :error)])}
       [:div.text-input-wrapper
        left-component]
       (error-block l-error-atom)]
      [:div.text-input-group__column
       {:class
        (u/bem :input-group [(when r-error-msg :error)])}
       [:div.text-input-wrapper
        right-component
        (when-not (or (:no-icon loptions) (:no-icon roptions))
          [:i.text-input-icon])]
       (error-block r-error-atom)]]

     (when (or (:hint loptions) (:hint roptions))
       [:div.form-hint-wrapper
        [:span.form-hint (str (:hint loptions) " " (:hint roptions))]])]))

(rum/defc split-input-field
  [[llabel lplaceholder latom & [loptions] :as left]
   [rlabel rplaceholder ratom & [roptions] :as right]]
  (let [l-val-atom (if (:yc-input? loptions) (rum/cursor latom :value) latom)
        r-val-atom (if (:yc-input? roptions) (rum/cursor ratom :value) ratom)
        l-error-atom (when (:yc-input? loptions) (rum/cursor latom :error-msg))
        r-error-atom (when (:yc-input? roptions) (rum/cursor ratom :error-msg))]
    (split-field
      [llabel
       (input-raw lplaceholder l-val-atom
         (u/maybe assoc
           loptions
           :error-atom l-error-atom))
       latom loptions]
      [rlabel
       (input-raw rplaceholder r-val-atom
         (u/maybe assoc
           roptions
           :error-atom r-error-atom))
       ratom roptions])))

(rum/defcs textarea-raw < rum/reactive
  {:will-mount
   (fn [state]
     (let [[val-atom {:keys [change-fn]}] (:rum/args state)
           local-atom (atom @val-atom)
           component (:rum/react-component state)]
         ;; Setup two-way atom binding
       (add-watch val-atom ::input
         (fn [k r o n]
           (when (and (not= o n) (not= @local-atom n))
             (reset! local-atom n)
             (rum/request-render component))))

       (cmn/debounce-atom local-atom 150
         (fn [n]
           (when (not= @val-atom n)
             (reset! val-atom n)
             (when change-fn
               (change-fn n)))))

       (assoc state ::value local-atom)))}
  [s value-atom & [{:keys [hint change-fn onblur-fn on-key-down limit required? disabled rows id placeholder wrap] :as options}]]
  (let [val-atom (::value s)]
    [:textarea.text-input.text-area
     (merge
       {:id id
        :name (:name options)
        :class (:class options)
        :rows rows
        :disabled disabled
        :required required?
        :placeholder placeholder
        :autoComplete "off"
        :spellCheck "false"
        :autoCorrect "off"
        :autoCapitalize "off"
        :wrap (or wrap "soft")
        :max-length limit}
       (input-props val-atom
         {:onblur-fn onblur-fn
          :on-key-down on-key-down
          :change-fn
          (fn [e v]
            (when (and limit (pos? limit) (> (count @val-atom) limit))
              (swap! val-atom #(subs % 0 limit))))}))]))

(defn upload-file [atm {:keys [path data allowed-types]} e {:keys [done-fn error-fn]}]
  #?(:cljs
     (let [file
           (first
             (-> e
               .-target
               .-files
               array-seq))

           form-data
           (doto
             (js/FormData.)
             (.append "file" file))]

       (when data
         (doseq [[k v] data]
           (.append form-data (name k) v)))

       (cond
         (and (and allowed-types file) (not (contains? allowed-types (.-type file))))
         (do
           (swap! atm assoc :success false :error "Error could not upload file")
           (error-fn))

         :else
         (ajax/POST
           path
           {:body form-data
            :response-format :json
            :keywords? true
            :handler
            (fn [res]
              (if res
                (do
                  (reset! atm res)
                  (done-fn res))
                (do
                  (swap! atm assoc :success false :error "Error could not upload file")
                  (error-fn))))
            :error-handler
            (fn [res]
              (swap! atm assoc :success false :error "Error could not upload file")
              (error-fn))
            :timeout 60000})))))

(rum/defcs raw-fileupload < rum/reactive (rum/local {} ::local)
  [s field-atom &
   [{:keys [yc-input? disabled? status/messages
            processing-fn done-fn error-fn small?
            allowed-types] :as opts}]]
  (let [local-atom (::local s)
        local (rum/react local-atom)
        val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        allowed-types (or allowed-types (u/get-allowed-types :all))]

    [:label
     {:class
      (u/bem :dope-file-input
        [(when (= (:status local) :uploading) :is-uploading)
         (when disabled? :disabled)
         (when small? :small)])}
     [:input.dope-file-input__field
      {:type "file"
       :disabled disabled?
       :accept (str/join "," allowed-types)
       :on-change
       (fn [e]
         (swap! local-atom assoc :status :uploading)
         (when processing-fn
           (processing-fn local-atom))
         (upload-file val-atom opts e
           {:done-fn
            (or
              (partial done-fn local-atom)
              #(swap! %1 assoc :status :uploaded))
            :error-fn
            (or
              (partial error-fn local-atom)
              #(swap! %1 assoc :status :error))}))
       :name (:name opts)}]

     (cond
       (= (:status local) :uploading)
       [:span.dope-file-input__text
        [:span.dope-file-input__icon
         [:i.u-animate-spin {:class (u/bem :icon [:2-1 :loading :inline])}]]
        [:span {:class (u/bem :loading-text [:is-absolute])} "Uploading"]]

       :else
       [:span.dope-file-input__text
        [:span.dope-file-input__icon
         [:i
          {:class
           (u/bem :icon [:2-1 :upload :block])}]]
        [:span
         (case (:status local)
           :uploaded (:uploaded messages "Upload a file")
           :error (:error messages "Error, re-upload file")
           (:default messages "Upload a file"))]])]))

;;TODO: Test how the feedback would populate the error atom
(rum/defcs fileupload < rum/reactive (rum/local {} ::local)
  [s label hint field-atom & [{:keys [yc-input?] :as opts}]]
  (let [local-atom (::local s)
        local (rum/react local-atom)
        val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        error-atom (when yc-input? (rum/cursor field-atom :error-msg))
        imageurl (cmn/curact val-atom :imageurl)]
    [:div
     {:class (u/bem :form-field [(when (= (:status local) :error) :error) (when (:flush? opts) :flush)])}

     [:div.file-input-wrapper
      (when-not (empty? label)
        [:span.form-label label ":"])

      (when (u/presence imageurl)
        [:div.dope-file-input-preview
         [:img.image--full
          {:src imageurl :alt ""}]])

      (raw-fileupload field-atom opts)]

     (when (u/presence hint)
       [:div.form-hint-wrapper [:span.form-hint hint]])

     (error-block error-atom)]))

(rum/defc textarea < rum/reactive
  [label field-atom & [{:keys [hint flush? required? change-fn limit disabled warning? yc-input?] :as options}]]
  (let [val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        error-atom (when yc-input? (rum/cursor field-atom :error-msg))
        error-msg (when error-atom (rum/react error-atom))]
    [:div
     {:class (u/bem :form-field [(when error-msg :error) (when flush? :flush) (when warning? :warning)])}
     (field-label label
       (u/maybe assoc options :error-atom error-atom))
     [:div.text-input-wrapper
      (textarea-raw val-atom options)]
     (when hint
       [:div.form-hint-wrapper
        [:span.form-hint hint]])
     (error-block error-atom)]))

(defn nodes->options [nodes val-atom]
  (map
    (fn [{:keys [text value children disabled] :as opt}]
      (if children
        [:optgroup {:label (or text value)}
         (nodes->options children val-atom)]
        [:option
         (u/maybe assoc
           {:value (or value text)}
           :disabled (when disabled true))
         (or text value)]))
    nodes))

(rum/defc select-raw < rum/reactive
  [val-atom option-tree & [{:keys [id change-fn hint on-dark small-width default required? disabled inline? tiny? small? sort? class] :as options}]]
  (let [v (rum/react val-atom)]
    (into
      [:select
       (u/maybe assoc
         (input-props val-atom
           {:type (:type options)
            :change-fn change-fn
            :default default})
         :class (u/bem :select [(when tiny? :tiny) (when small? :small) (when inline? :inline) (when sort? :sort-results)] class)
         :required required?
         :disabled disabled
         :name (:name options)
         :id id)]
      (nodes->options option-tree val-atom))))

(rum/defc select < rum/reactive
  [label field-atom option-tree &
   [{:keys
     [field-classes change-fn hint
      on-dark small-width default tiny? small? flush? error yc-input? class] :as options}]]
  (let [val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        error-atom (when yc-input? (rum/cursor field-atom :error-msg))]
    (form-field label
      (u/maybe assoc options :error-atom error-atom)
      [:div.select-wrapper
       {:class (when small-width "u-max-width-128")}
       (select-raw val-atom option-tree options tiny? small?)
       [:i.select-icon]]
      (when hint
        [:div.form-hint-wrapper
         [:span.form-hint
          (if coll? hint (str hint))]])
      (error-block error-atom))))

(rum/defc checkbox-raw < rum/reactive
  [val-atom & [{:keys [change-fn pre-change-fn disabled required?]}]]
  (let [props (checkbox-props val-atom change-fn pre-change-fn disabled required?)]
    [:input.checkbox (merge props {:value 1})]))

(rum/defc checkbox < rum/reactive
  [label field-atom &
   [{:keys [change-fn pre-change-fn description id on-dark
            default-checked disabled inline required? small? yc-input?] :as options}]]
  (when (and yc-input? (nil? @field-atom))
    (swap! field-atom assoc :value (boolean default-checked)))
  (let [val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        props (checkbox-props val-atom change-fn pre-change-fn disabled required?)]
    [:label
     {:on-click (when-not disabled (:on-change props))
      :class (u/bem :checkbox-label [(when inline :inline)] (when on-dark :u-text-white) (:class options))
      :style (merge {} (:style options))}
     [:input.checkbox
      (merge
        props
        (u/maybe assoc
          {:value 1 :id id :name (:name options) :required (or required? false)}))]
     (u/wrap-element
       (not inline)
       [:span.checkbox__graphic-wrapper]
       (list
         [:span
          {:class (u/bem :checkbox__graphic [(when inline :inline)])}]))
     [:span
      {:class (u/bem :checkbox__text [(when inline :inline)])}
      [:span
       {:class (u/bem :checkbox__title [(when small? :small)])}
       label]
      [:span.u-hidden-from-view "."]
      (when description
        [:span
         {:class (u/bem :checkbox__description [(when small? :small)])}
         description])]]))

(rum/defc checkboxes < rum/reactive [field-atom checkbox-list {:keys [yc-input?] :as opts}]
  (let [error-atom (when yc-input? (rum/cursor field-atom :error-msg))
        error-msg (when error-atom (rum/react error-atom))]
    [:div
     {:class (u/bem :form-fieldset [(when error-msg :error)])}
     (into
       ;; TODO: Jon says: Besides the overall setup of form components not being great to start
       ;;       with, why are we pushing these into a <div>? What purpose does that <div> serve?
       ;;       It makes it incredibly difficult to predict and style the outcome with CSS.
       [:div]
       (map
         (fn [{:keys [atom-path label options]}]
           (let [checkbox-atom (rum/cursor field-atom atom-path)]
             (checkbox label checkbox-atom (merge options opts))))
         checkbox-list))
     (error-block error-atom)]))

;;todo - maybe we can use raw-checkbox - fix once checked=true attribute is working...
(rum/defc checkbox-variation-picker < rum/reactive
  [val-atom & [{:keys [change-fn pre-change-fn description colour id on-dark default-checked disabled inline required? small?] :as options}]]
  (let [props (checkbox-props val-atom change-fn pre-change-fn disabled required?)]
    [:label.var-checkbox
     [:input.var-checkbox__input.u-hidden-from-view
      (merge
        props
        (u/maybe assoc
          {:value 1 :id id :name (:name options) :required (or required? false)}))]

     [:span.var-checkbox__button
      (when (u/presence colour)
        [:span.var-checkbox__graphic-wrapper
         [:span.var-checkbox__graphic
          {:style {:background-color (str "#" colour)}}]])
      [:span.var-checkbox__text
       description]]]))

(rum/defc toggle < rum/reactive
  [label val-atom &
   [{:keys [change-fn description id on-dark on-change
            default-checked disabled inline required? small? right? form-field?] :as options}]]
  (let [props (toggle-props val-atom default-checked change-fn disabled required? on-change)
        select-element
        (u/wrap-element
          (not inline)
          [:span.toggle__graphic-wrapper]
          (list
            [:span
             {:class (u/bem :toggle__graphic [(when inline :inline)])}]))]
    [:label
     {:class (u/bem :toggle-label [(when inline :inline)] (when on-dark :u-text-white)
               (when form-field? :form-field))}
     [:input.toggle
      (merge
        props
        (u/maybe assoc
          {:value 1 :id id
           :name (:name options)
           :required (or required? false)}))]
     (when-not right? select-element)
     [:span
      {:class (u/bem :toggle__text [(when inline :inline)] (when right? :u-text-right))}
      [:span
       {:class (u/bem :toggle__title [(when small? :small)])}
       label]
      [:span.u-hidden-from-view "."]
      (when description
        [:span
         {:class (u/bem :toggle__description [(when small? :small)])}
         description])]
     (when right? select-element)]))

(defn radiobox-props
  [atm value rbx-name {:keys [disabled? default-checked? change-fn required?]}]
  (let [prop-value (rum/react atm)]
    {:checked
     (boolean
       (if (and (nil? prop-value) default-checked?)
         (reset! atm value)
         (= value prop-value)))
     :type "radio"
     :disabled disabled?
     :value (or value false)
     :name rbx-name
     :required (or required? false)
     :on-change
     (fn [e]
       (reset! atm value)
       (when change-fn
         (change-fn e)))}))

(rum/defc radio < rum/reactive
  [label value val-atom & [{:keys [change-fn description on-dark disabled inline required default-checked?] :as opts}]]
  (let [props
        (radiobox-props val-atom value (:name opts)
          {:disabled? (or disabled false)
           :change-fn change-fn
           :required? required
           :default-checked? default-checked?})]
    [:label.radio-label
     {:on-click (:on-click props)
      :class
      (u/bem
        (when on-dark :u-text-white)
        (when disabled :radio-label--disabled)
        (when inline :radio-label--inline) [])}
     [:input.radio props]
     [:span.radio__graphic-wrapper [:span.radio__graphic]]
     [:span.radio__text
      [:span.radio__title label]
      [:span.u-hidden-from-view "."]
      (when description
        [:span.radio__description description])]]))

(rum/defc radio-flex < rum/reactive [val-atom {:keys [label value options]} column-number]
  [:div
   {:class (u/bem :flex-grid-block [(keyword (str "m-1-" column-number))])}
   [:div.flex-grid-content
    (radio label value val-atom options)]])

(rum/defc radios < rum/reactive
  [field-atom radio-list {:keys [yc-input? column-number inline] :as opts}]
  (let [val-atom (if yc-input? (rum/cursor field-atom :value) field-atom)
        error-atom (when yc-input? (rum/cursor field-atom :error-msg))
        error-msg (when error-atom (rum/react error-atom))
        radios-gpd (partition-all (or column-number 1) radio-list)]

    [:div
     {:class (u/bem :form-field [(when error-msg :error)])}
     (map-indexed
       (fn [idx radio-fields]
         (into
           [:div
            {:class
             (u/bem :flex-grid-group
               [:flush-force
                (when inline :inline)
                (when inline :align-center)
                (when (not inline) :align-left)
                (when (not inline) :is-vertical)])}]
           (map-indexed
             (fn [child-idx radio-field]
               (rum/with-key
                 (radio-flex val-atom (assoc-in radio-field [:options :inline] inline) column-number)
                 (str idx "-" child-idx)))
             radio-fields)))
       radios-gpd)
     (error-block error-atom)]))

(rum/defc progress-steps < rum/reactive
  [progress-atom]
  (let [{:keys [total current]} (cmn/curval progress-atom)
        width (int (* (/ (:idx current 0) total) 100))]
    [:aside.progress-bar-wrapper
     [:div.progress-bar
      [:div.progress-bar__value
       {:style {:width (str width "%")}}
       [:span.u-hidden-from-view (str width "% progress")]]]
     [:p.progress-bar__label "Step " (:idx current 0) " of " total " – " (:title current)]]))

(defn setup-steps [steps]
  (let [index
        (->>
          steps
          (map-indexed
            (fn [idx s]
              [(:key s) (assoc s :idx (or (:step s) (inc idx)))]))
          (into {}))
        total
        (if (:step (first steps))
          (count (distinct (map :step steps)))
          (count steps))]

    {:total total
     :index index
     :current (get index (:key (first steps)))}))

(defn progress-step [state k]
  (assoc state :current (get-in state [:index k])))

(rum/defcs inline-autocomplete-field
  < (cmn/local {:open? false} ::local) rum/reactive
  [s current-value suggestion-atom
   {:keys [suggestion-hint empty-list-hint change-fn select-fn item-render-fn min-count initial-value
           select-style? force-open? deselect-fn small? close-on-select?]
    :or {close-on-select? true}}]
  (assert (not (nil? select-fn)) "Nil select-fn passed to inline-autocomplete-field, problems.")
  (let [local-atom (::local s)
        {:keys [open?]} (rum/react local-atom)
        val-atom (rum/cursor-in local-atom [:input])
        search-atom (rum/cursor local-atom :search)
        i (rum/react val-atom)]

    [:div.u-relative
     {:on-mouse-down
      #(when-not open?
         (swap! local-atom assoc :open? true :input "")
         (reset! suggestion-atom initial-value)
         (.preventDefault %))}

     (if select-style?
       [:div.select-wrapper
        [:i.select-icon]
        [:div
         {:tab-index 0
          :class (u/bem :select [(when small? :small)])
          :on-mouse-down
          #(when open?
             (swap! local-atom assoc :open? false :input ""))
          :on-key-down
          (fn [e]
            (when
              (and
                select-style?
                (not (contains? #{9 16} (.-keyCode e)))
                (not open?))
              (swap! local-atom assoc :open? true :input "")
              (reset! suggestion-atom initial-value)
              #_(.preventDefault e)))}
         current-value]]
       current-value)

     (when open?
       (let [suggestions (rum/react suggestion-atom)
             selected (cmn/curact local-atom :selected)
             prev-selected (cmn/curact local-atom :prev-selected)
             same-select? (= prev-selected selected)

             select-action-fn
             (fn [select-option]
               (if (and deselect-fn same-select?)
                 (do
                   (swap! local-atom dissoc :prev-selected)
                   (deselect-fn select-option))
                 (do
                   (if close-on-select?
                     (swap! local-atom assoc
                       :open? false
                       :input ""
                       :prev-selected (:selected @local-atom))
                     (swap! local-atom assoc :prev-selected (:selected @local-atom)))
                   (select-fn select-option))))

             searching?
             (and
               (>= (count (or i "")) (or min-count 2))
               (nil? (rum/react suggestion-atom)))]
         ;; TODO: Feels weird here, triggers a rerender before this one is even done.
         (when
           (and
             (not-empty suggestions)
             (or
               (nil? selected)
               (not (contains? (set suggestions) selected))))
           (swap! local-atom assoc :selected (first suggestions) :selected-idx 0))

         [:div.admin-search-suggest-wrapper
          (into
            [:ul.admin-search-suggest
             [:li.admin-search-suggest__item
              (input-raw
                suggestion-hint
                val-atom
                {:onblur-fn
                 (fn [_]
                   ;; Restore global keys
                   (cmn/restore-global-keys)
                   (when-not force-open?
                     (swap! local-atom assoc :open? false)))

                 :auto-focus true
                 :on-focus
                 (fn [e]
                   (cmn/override-global-keys))

                 :on-key-down
                 (fn [e _]
                   (cond
                     (== (.-keyCode e) 13)
                     (do
                       (select-action-fn selected)
                       (cmn/restore-global-keys)
                       (swap! local-atom assoc :open? false)
                       (.preventDefault e))

                     (== (.-keyCode e) 27)
                     (do
                       (cmn/restore-global-keys)
                       (swap! local-atom assoc :open? false)
                       (.preventDefault e))

                     ;; down
                     (== (.-keyCode e) 40)
                     (do
                       (swap! local-atom
                         (when-not (empty? suggestions)
                           (fn [l]
                             (let [new-idx (mod (inc (:selected-idx l)) (count suggestions))]
                               (assoc l
                                 :selected (nth suggestions new-idx)
                                 :selected-idx new-idx)))))

                       (.preventDefault e))

                     ;; up
                     (== (.-keyCode e) 38)
                     (do
                       (swap! local-atom
                         (fn [l]
                           (when-not (empty? suggestions)
                             (let [new-idx (mod (dec (:selected-idx l)) (count suggestions))]
                               (assoc l
                                 :selected (nth suggestions new-idx)
                                 :selected-idx new-idx)))))

                       (.preventDefault e))))

                 :change-fn
                 (fn [_ i]
                   (let [current-count (count i)
                         search-count (rum/cursor search-atom :count)]
                     (cond
                       (>= current-count min-count)
                       (do
                         (swap! search-atom assoc
                           :term i
                           :count current-count)
                         (swap! local-atom dissoc :selected :selected-idx)
                         (change-fn i))

                       (and
                         (<= @search-count min-count)
                         (< current-count min-count))
                       (do
                         (reset! suggestion-atom "")
                         (swap! local-atom dissoc :selected :selected-idx)
                         (change-fn "")))))})]]

            (cond
              (and (not (u/presence i)) (empty? suggestions))
              nil

              searching?
              [[:li.admin-search-suggest__item
                [:a.admin-search-suggest__link
                 "Searching..."]]]

              (empty? suggestions)
              [[:li.admin-search-suggest__item
                [:a.admin-search-suggest__link
                 empty-list-hint]]]

              :else
              (map-indexed
                (fn [idx suggestion]
                  [:li.admin-search-suggest__item
                   [:a
                    {:class
                     (u/bem :admin-search-suggest__link
                       ;;When the condition is met, the entire list is re-rendered...
                       [(when (= suggestion selected) :selected)])

                     :on-mouse-over
                     (fn [e]
                       (swap! local-atom assoc :selected suggestion :selected-idx idx))

                     :on-mouse-down
                     (fn [_]
                       (select-action-fn selected))}
                    (if item-render-fn
                      (item-render-fn suggestion)
                      suggestion)]])
                suggestions)))]))]))

(defn inline-autocomplete-select
  [current-value suggestion-list
   {:keys
    [suggestion-hint empty-list-hint
     filter-fn select-fn item-render-fn min-count match-count
     initial-list change-fn close-on-select?] :as options
    :or {match-count 20}}]
  (let [initial (or initial-list (take match-count suggestion-list))
        suggestion-atom (atom initial)
        filter-with
        (or filter-fn #(str/includes? (str/lower-case %1) (str/lower-case %2)))]
    (inline-autocomplete-field
      current-value suggestion-atom
      (merge
        options
        {:min-count (or min-count 0)
         :initial-value initial
         :change-fn
         (fn [v]
           (when v
             (reset! suggestion-atom
               (->>
                 suggestion-list
                 (filter #(filter-with % v))
                 (take match-count)))))}))))

(defn form-validate [node-atom success-fn]
  (fn [e]
    (if (.checkValidity @node-atom)
      (success-fn e)
      (.reportValidity @node-atom))))

(def test-steps
  (setup-steps
    [{:key :step1 :title "This is the first step"}
     {:key :step2 :title "You made it to the second step!"}
     {:key :step3 :title "Another Step!"}
     {:key :step4 :title "Cash in!"}
     {:key :step5 :title "Just checking if you're still here..."}]))

(defonce local-atom (atom {:steps test-steps :selected-option 1}))

(rum/defcs tiny-demo [s]
  [:div
   (progress-steps (rum/cursor local-atom :steps))

   (into [:div]
     (map
       (fn [b]
         (buttons/button
           (in/capitalize (u/safe-name (:key b)))
           (fn [] (swap! local-atom update :steps progress-step (:key b)))
           {:disabled
            #(= (cmn/curact local-atom [:steps :current :key]) (:key b))}))
       (vals (:index test-steps))))

   (input "Text" "placeeholder" (rum/cursor local-atom :basic1))
   [:h1 "State: " (cmn/label local-atom :basic1)]])

;; ========= Form Demo Modal ===========================
(rum/defcs form-demo < rum/reactive (rum/local {} ::state)
  [s {:keys [form-atom yc-input?]}]
  (let [form-atom (if form-atom form-atom (::state s))]
    (println "form-demo")
    (println @form-atom)
    [:div.layout-component-demo
     [:div.form
      [:p.u-text-center
       (progress-steps (rum/cursor form-atom :steps))
       (into [:div]
         (map
           (fn [b]
             (buttons/button
               (in/capitalize (u/safe-name (:key b)))
               (fn [] (swap! form-atom update :steps progress-step (:key b)))
               {:disabled
                #(= (cmn/curact form-atom [:steps :current :key]) (:key b))}))
           (vals (:index test-steps))))]

      (input "Text Input Label:" "e.g. Placeholder Text"
        (rum/cursor form-atom :basic1)
        (u/maybe assoc
          {:hint
           ["You have typed: "
            (cmn/label form-atom :basic1)]}
          :yc-input? yc-input?))

      (input "Email Address:" "e.g. jane.smith@domain.com"
        (rum/cursor form-atom :email)
        (u/maybe assoc
          {:type :email
           :hint
           ["You have typed: "
            (cmn/label form-atom :email)]}
          :yc-input? yc-input?))

      (input "User Name:" "e.g. janesmith81"
        (rum/cursor form-atom :username)
        (u/maybe assoc
          {:type :user
           :required? true
           :hint ["Logging in with: " (cmn/label form-atom :username)]}
          :yc-input? yc-input?))

      (input "Password:" ""
        (rum/cursor form-atom :password)
        (u/maybe assoc
          {:type :password
           :hint ["You have typed a password of length: " (cmn/label #(count (cmn/curact form-atom :password)))]}
          :yc-input? yc-input?))

      (input "Scan Barcode:" ""
        (rum/cursor form-atom :barcode)
        (u/maybe assoc
          {:type :barcode
           :hint ["Scanned: " (cmn/label form-atom :barcode)]}
          :yc-input? yc-input?))

      (input "Amount (Rand):" ""
        (rum/cursor form-atom :amount)
        (u/maybe assoc
          {:type :currency
           :hint ["Confirmed amount: R" (cmn/label #(u/number-format (+ 100 (or (cmn/curact form-atom :amount) 0)) 2))]}
          :yc-input? yc-input?))

      (split-input-field
        ["First Name:" "e.g. Jane" (rum/cursor form-atom :firstname)
         (u/maybe assoc
           {:type :user :hint "This is some hint text"}
           :yc-input? yc-input?)]
        ["Last Name:" "e.g. Smith" (rum/cursor form-atom :lastname)
         (u/maybe assoc
           {:type :user}
           :yc-input? yc-input?)])

      (textarea "Your Message:" (rum/cursor form-atom :message)
        (u/maybe assoc
          {:hint ["You have " (cmn/label #(in/pluralize (- 1000 (count (cmn/curact form-atom :message))) "character")) " left"]
           :limit 1000}
          :yc-input? yc-input?))

      (select "Select:" (rum/cursor form-atom :select)
        [{:text "None" :value 0}
         {:text "The first bit"
          :value 1
          :children
          [{:value 2 :text "This is an option that is super duper long and will hopefully be cut off"}
           {:value 3 :text "This is another option"}
           {:value 4 :text "And yes, another option"}]}
         {:text "The second bit"
          :value 5
          :children
          [{:value 6 :text "This is an option"}
           {:value 7 :text "This is another option"}
           {:value 8 :text "And yes, another option"}]}
         {:value 9 :text "This is an option"}
         {:value 10 :text "This is another option"}
         {:value 11 :text "And yes, another option"}]
        (u/maybe assoc
          {:hint ["Selected option ID: " (cmn/label form-atom (if yc-input? [:select :value] :select))]}
          :yc-input? yc-input?))

      (select "Default test:" (rum/cursor form-atom :select-default)
        [{:text "Skip" :value 0}
         {:text "Correct" :value 1}
         {:text "Incorrect" :value 2}]
        (u/maybe assoc
          {:default 1
           :hint ["Selected option ID: " (cmn/label form-atom (if yc-input? [:select-default :value] :select-default))]}
          :yc-input? yc-input?))

      (select "Preset test:" (rum/cursor form-atom :selected-option)
        [{:text "Skip" :value 0}
         {:text "Correct" :value 1}
         {:text "Incorrect" :value 2}]
        (u/maybe assoc
          {:hint ["Selected option ID: " (cmn/label form-atom (if yc-input? [:selected-option :value] :selected-option))]}
          :yc-input? yc-input?))

      #_(checkbox "Checkbox title"
          (rum/cursor form-atom :checkbox)
          {:description
           (cmn/label
             #(if (cmn/curact form-atom :checkbox)
                "Enjoy!"
                "An optional description of what this means."))})

      (checkboxes
        (rum/cursor form-atom :checkbox)
        [{:label "Dog" :atom-path :dog :options {:description "Best friend"}}
         {:label "Cat" :atom-path :cat :options {:description "Floofball"}}
         {:label "Rabbit" :atom-path :rabbit :options {:description "Carrots..?"}}]
        {:yc-input? yc-input?})

      (radios
        (rum/cursor form-atom :radio)
        [{:label "Candidate 1" :value 1 :options
          {:description
           (cmn/label
             #(case (cmn/curact form-atom (if yc-input? [:radio :value] :radio))
                1 "Thank you, infinite wealth and fame is yours!"
                2 "Why him?"
                "Pick me!"))}}
         {:label "Candidate 2" :value 2 :options
          {:description
           (cmn/label
             #(case (cmn/curact form-atom (if yc-input? [:radio :value] :radio))
                2 "Sweet, housing for everyone!"
                1 "No, seriously, pick me!"
                "No, Pick me!!"))}}
         {:label "Candidate 3" :value 3 :options {:description "Who dis..?"}}]
        {:yc-input? yc-input?})

      #_(radio "Candidate 1" 1 (rum/cursor form-atom :radio)
          {:description
           (cmn/label
             #(case (cmn/curact form-atom :radio)
                1 "Thank you, infinite wealth and fame is yours!"
                2 "Why him?"
                "Pick me!"))})

      #_(radio "Candidate 2" 2 (rum/cursor form-atom :radio)
          {:description
           (cmn/label
             #(case (cmn/curact form-atom :radio)
                2 "Sweet, housing for everyone!"
                1 "No, seriously, pick me!"
                "No, Pick me!!"))})]]))
